import React, { useEffect, useMemo } from "react";
import _ from 'lodash';
import cx from "classnames";

import Slider from "src/components/slider";

import styles from './collaborate.module.scss';
import { Checkbox, SearchBar } from "src/components/input";
import { useSelector } from "react-redux";
import Button from "src/components/button";
import { useDispatch } from "react-redux";
import InfiniteScroll from "src/components/infiniteScroll";
import { FormCheck } from "react-bootstrap";
import EmptyMessage from "src/components/emptyMessage";
import Tooltip from "src/components/tooltip";
import Truncate from "src/components/truncate";
import { DEAL_ROOM_EXPERIENCE } from "src/constants/collections";
import LinesEllipsis from "react-lines-ellipsis";
import { fetchCollaboratorsForDealRoom, fetchStakeholders, invalidateDealRoomCollaborators, updateCollaboratorsForDealRoom } from "src/redux/actions/dealRoom";
import { DealRoom, DealRoomCollaborate, StakeHolderTypeEnum } from "src/types/dealRoom";

interface UpdateState {
  [key: string]: {
    read: boolean;
    write: boolean;
  };
}

interface CollaborateDialogProps {
  show: boolean;
  dealRoom: DealRoom;
  onClose: () => void;
}

export const CollaborateDialog: React.FC<CollaborateDialogProps> = ({ show, dealRoom, onClose }) => {
  const dispatch = useDispatch();

  const [query, setQuery] = React.useState<string | null>('');
  const [showCollaborators, setShowCollaborators] = React.useState<boolean>(false);
  const [changes, setChanges] = React.useState<UpdateState>({});
  const account = useSelector((state) => _.get(state, 'accounts.item', {}));

  const { updating, updated } = useSelector((state: any) => {
    const collaboratorState = _.get(state, `dealRooms.dealRoom['${dealRoom.id}'].collaborators`);
    const updating = _.get(collaboratorState, 'updating');
    const updated = _.get(collaboratorState, 'updated');

    return {
      updating,
      updated
    }
  }, _.isEqual);

  useEffect(() => {
    setQuery('');
    setShowCollaborators(false);
    setChanges({});
  }, [show]);

  useEffect(() => {
    if (updated) {
      dispatch(fetchStakeholders({
        dealRoomId: dealRoom.id || "",
        type: StakeHolderTypeEnum.INTERNAL,
        page: 0
      }))
      handleClose();
    }
  }, [updated]);

  const handleQueryChange = (value) => {
    setQuery(value);
  }

  const handleFinish = () => {
    const requestBody = {
      collaboratorsList: _.map(changes, (value, key) => {
        const [entityType, id] = key.split('-');
        return {
          id: id,
          canWrite: value.write,
          canRead: value.read
        }
      })
    } as any;

    dispatch(updateCollaboratorsForDealRoom({
      dealRoom: dealRoom.id!,
      requestBody
    }))
  }

  const handleClose = () => {
    dispatch(invalidateDealRoomCollaborators(dealRoom.id!));
    onClose();
  }

  const handleChange = (collaborator: DealRoomCollaborate) => {
    const id = `${collaborator.entityType}-${collaborator.user?.id}`;
    const updated = {
      ...changes,
      [id]: {
        read: collaborator.read,
        write: collaborator.write
      }
    }

    setChanges(updated);
  }

  return (
    <Slider.Dialog show={show} width={'400px'}
      className={cx(styles.confirmationDialog)}
      onClose={onClose}
    >
      <Slider.Body className={styles.body}>
        <div className={styles.header}>
          <h5 className={styles.title}>add internal stakeholders</h5>
          <p className={styles.subtitle}>invite users from {_.get(account,"name","")} to this deal room</p>
        </div>
        <div className={styles.content}> 
           <div className={styles.searchBar}>
            <SearchBar query={query} onSearch={handleQueryChange} />
          </div>
          <div className={cx(styles.stacks, styles.content)}>
            <UserBrowser dealRoom={dealRoom}
              query={query || ""}
              showCollaborators={showCollaborators}
              changes={changes}
              onChange={handleChange}
            />
          </div>
        </div>
      </Slider.Body>
      <Slider.Actions>
        <Button
          variant="primary"
          icon="paperflite-tick"
          text={updating ? "Updating..." : "Update"}
          disabled={updating}
          onClick={handleFinish}
        />
        <Button
          variant="secondary"
          text="Cancel"
          disabled={updating}
          onClick={handleClose}
        />
      </Slider.Actions>
    </Slider.Dialog>
  );
}

interface UserBrowserProps {
  dealRoom: DealRoom;
  query: string;
  showCollaborators: boolean;
  changes: UpdateState;
  // eslint-disable-next-line no-unused-vars
  onChange: (collaborator: DealRoomCollaborate) => void;
}

const UserBrowser: React.FC<UserBrowserProps> = ({ query, dealRoom, showCollaborators, changes, onChange }) => {
  const dispatch = useDispatch();

  const { loading, collaborators, hasMoreItems, nextPage } = useSelector((state: any) => {
    const collaboratorState = _.get(state, `dealRooms.dealRoom['${dealRoom.id}'].collaborators`);
    const collaborators = _.get(collaboratorState, 'items') || [];
    const loading = _.get(collaboratorState, 'loading');
    const pagination = _.get(collaboratorState, 'pagination');
    const totalPages = _.get(pagination, "totalPages", 0);
    const totalElements = _.get(pagination, "totalRecordsCount", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");

    let nextPage = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage >= totalPages) {
      nextPage = null;
    }

    return {
      loading,
      collaborators,
      totalElements,
      hasMoreItems,
      nextPage
    }
  }, _.isEqual);

  const user = useSelector((state: any) => _.get(state, 'users.me.id'));

  const empty = _.size(collaborators) === 0 && !loading;

  useEffect(() => {
    // Fetch users and user groups
    fetchCollaboratorsForPage(0);
  }, [showCollaborators, query]);

  const fetchCollaboratorsForPage = (page) => {
      dispatch(fetchCollaboratorsForDealRoom({
        dealRoom: dealRoom.id!,
        query: query,
        page: page
      }));
  }

  const handleLoadCollaborators = () => {
    if (nextPage) {
      fetchCollaboratorsForPage(nextPage);
    }
  }

  if (empty) {
    return (
      <div className={styles.empty}>
        <EmptyMessage
          image={'/images/collections/empty_collaborators.svg'}
          heading="Uh oh!"
          message="The user(s) that you were looking for was not found."
        />
      </div>
    );
  }

  return (
    <div className={cx(styles.assetBrowser, styles.stack)}>
      <InfiniteScroll
        className={styles.scroller}
        loading={loading}
        hasMore={hasMoreItems}
        loadMore={handleLoadCollaborators}
        loader={() => {
          return (
            <>
              {_.times(_.size(collaborators) === 0 ? 3 : 1, (index) => (
                <UserPlaceholder key={index} />
              ))}
            </>
          )
        }}
      >
        {collaborators.map((collaborator) => {
          const entityType = collaborator.entityType;

          if (entityType === 'user') {
            const owner = collaborator.user?.id === user;
            const id = `${entityType}-${collaborator.user?.id}`;
            const isDeactivated = !_.get(collaborator, 'user.enabled', false);
            const changed = changes[id];
            if (changed) {
              collaborator = {
                ...collaborator,
                read: changed.read,
                write: changed.write
              }
            }

            return (
              <UserRow key={collaborator.user?.id}
                collaborator={collaborator}
                owner={owner}
                onChange={onChange}
                isDeactivated={isDeactivated}
              />
            );
          }

          return false;
        })}
      </InfiniteScroll>
    </div>
  );
}

interface UserRowProps {
  collaborator: DealRoomCollaborate;
  owner?: boolean;
  // eslint-disable-next-line no-unused-vars
  onChange: (collaborator: DealRoomCollaborate) => void;
  isDeactivated?: boolean;
}

const UserRow: React.FC<UserRowProps> = ({ collaborator, owner, onChange, isDeactivated }) => {

  const canRead = collaborator.read || owner!;
  const canWrite = collaborator.write || owner!;
  const name = collaborator.user?.firstName + ' ' + collaborator.user?.lastName;
  const [clamped, setClamped] = React.useState<boolean>(false);

  const handleReadChange = (value) => {
    onChange({
      ...collaborator,
      read: value
    });
  }

  const handleWriteChange = (value) => {
    onChange({
      ...collaborator,
      read: value ? true : collaborator.read,
      write: value
    });
  }

  return (
    <div className={cx(styles.row, styles.user, { [styles.owner]: owner })}>
      <div
        className={cx(styles.details, {
          [styles.deactivatedUser]: isDeactivated,
        })}
      >
        <i
          className={cx({
            "paperflite-user-settings": !isDeactivated,
            "fa fa-ban": isDeactivated,
          })} />
        <Tooltip placement="top" content={name}>
          <span>
            <Truncate width={owner ? 150 : 200} className={cx(styles.name, { [styles.deactivatedName]: isDeactivated })} lines={1}>
              {name}
            </Truncate>
          </span>
        </Tooltip>
          {isDeactivated && (
            <div className={styles.deactivatedTag}>&nbsp;(Deactivated)</div>
          )}
          {owner && <div className={styles.tag}>OWNER</div>}
      </div>
      {!isDeactivated && (
      <div className={styles.actions}>
        <Checkbox className={styles.action}
          label="view"
          checked={canRead || canWrite}
          disabled={owner || canWrite}
          onChange={handleReadChange}

        />
        <div className={styles.separator}></div>
        <Checkbox className={styles.action}
          label="edit"
          checked={canWrite}
          disabled={owner}
          onChange={handleWriteChange}
        />
      </div>
      )}
    </div>
  );
}

const UserPlaceholder: React.FC = () => {
  return (
    <div className={cx(styles.row, styles.placeholder)}>
      <div className={styles.details}>
        <div className={cx(styles.name, styles.block)}></div>
      </div>
      <div className={styles.actions}>
        <div className={cx(styles.action)}>
          <div className={cx(styles.circle, styles.block)}></div>
          <div className={cx(styles.text, styles.block)}></div>
        </div>
        <div className={styles.separator}></div>
        <div className={cx(styles.action)}>
          <div className={cx(styles.circle, styles.block)}></div>
          <div className={cx(styles.text, styles.block)}></div>
        </div>
      </div>
    </div>
  );
}